import { Link, useNavigate } from 'react-router-dom';
import IconSvg from 'src/assets/icons/IconSvg';
import Button from 'src/components/button/Button';
import NotificationsDropdown from 'src/components/dropdowns/NotificationsDropdown';
import ShoppingCartDropdown from 'src/components/dropdowns/ShoppingCartDropdown';
import { useViewport } from '../../context/ViewportContext';
import { useState } from 'react';
import SelectWalletModal from 'src/components/modal/SelectWalletModal';
import { useWallet } from 'src/hooks';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

const AppHeader = () => {
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSelectWalletOpen, setIsSelectWalletOpen] = useState(false);
  const { isConnected, displayAddress, disconnect, paymentAddress } = useWallet();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const redirectAndCloseMenu = (path: string) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  return (
    <div>
      <SelectWalletModal isOpen={isSelectWalletOpen} close={() => setIsSelectWalletOpen(false)} />

      <div className="bg-grayscale-700 xl:py-5 py-4 2xl:px-[104px] px-5 flex justify-between items-center fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center gap-[52px]" onClick={() => setIsMenuOpen(false)}>
          <div className="cursor-pointer" onClick={() => navigate('/home')}>
            <IconSvg icon={isMobile ? 'appLogoM' : 'appLogoL'} />
          </div>

          <div className="items-center gap-[25px] xl:flex hidden">
            <Link
              to="/raffle"
              className="flex gap-2.5 items-center px-4 py-2 font-semibold text-lg text-grayscale-300"
              onClick={() => setIsMenuOpen(false)}
            >
              <IconSvg icon="ticketM" />

              <span>Raffles</span>
            </Link>

            <Link
              to="/leaderboard"
              className="flex gap-2.5 items-center px-4 py-2 font-semibold text-lg text-grayscale-300"
              onClick={() => setIsMenuOpen(false)}
            >
              <IconSvg icon="leaderboardM" />

              <span>Leaderboard</span>
            </Link>

            <button className="flex gap-2.5 items-center px-4 py-2 font-semibold text-lg text-brand-granite">
              <span>[Redacted]</span>
            </button>
          </div>
        </div>

        <div className="flex gap-10 items-center">
          <div className="flex gap-2 items-center xl:gap-4" onClick={() => setIsMenuOpen(false)}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => redirectAndCloseMenu('/raffle/create')}
              className="hidden xl:block"
            >
              Create Raffle
            </Button>

            {isConnected ? (
              <Menu as="div" className="relative hidden xl:block">
                <MenuButton className="flex items-center gap-2 h-[42px] px-4 rounded-lg bg-grayscale-900 text-brand-magnolia text-lg font-semibold">
                  <IconSvg icon="walletS" className="text-brand-tangerine" />
                  <span>{displayAddress}</span>
                </MenuButton>

                <MenuItems
                  transition
                  anchor="bottom end"
                  className="z-50 mt-1.5 w-[200px] rounded-lg bg-grayscale-900 p-2 space-y-1"
                >
                  <MenuItem>
                    <button
                      className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-grayscale-300 hover:bg-grayscale-800 transition-colors"
                      onClick={() => navigate('/profile')}
                    >
                      <IconSvg icon="userS" className="text-grayscale-400" />
                      <span>My Profile</span>
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-grayscale-300 hover:bg-grayscale-800 transition-colors"
                      onClick={() => {
                        navigator.clipboard.writeText(paymentAddress?.address || '');
                      }}
                    >
                      <IconSvg icon="copyS" className="text-grayscale-400" />
                      <span>Copy Address</span>
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-grayscale-300 hover:bg-grayscale-800 transition-colors"
                      onClick={disconnect}
                    >
                      <IconSvg icon="logoutS" className="text-grayscale-400" />
                      <span>Log out</span>
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                className="hidden xl:block"
                onClick={() => setIsSelectWalletOpen(true)}
              >
                Connect Wallet
              </Button>
            )}

            <Link
              to="/raffle/create"
              className="text-brand-black bg-brand-tangerine p-2.5 rounded-lg xl:hidden"
              onClick={() => setIsMenuOpen(false)}
            >
              <IconSvg icon="plusS" />
            </Link>

            <button
              className={`bg-grayscale-900 p-2.5 rounded-lg xl:hidden ${
                isConnected ? 'text-system-success' : 'text-brand-tangerine'
              }`}
              onClick={() => !isConnected && setIsSelectWalletOpen(true)}
            >
              <IconSvg icon="walletS" />
            </button>

            <ShoppingCartDropdown />

            <NotificationsDropdown />
          </div>

          <button className="xl:hidden text-grayscale-400" onClick={toggleMenu}>
            <IconSvg icon="menuM" />
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="xl:hidden fixed top-[68px] z-50 w-full bottom-[76px] flex flex-col">
          <div className="z-10 px-5 py-6 rounded-b-lg bg-brand-black">
            <div className="space-y-3">
              {isConnected ? (
                <div className="flex gap-2 items-center">
                  <button className="flex items-center h-[34px] rounded-md bg-grayscale-900 gap-2 px-3">
                    <div className="flex justify-center items-center w-6 h-6">
                      <IconSvg icon="walletS" className="text-brand-tangerine" />
                    </div>

                    <span className="text-sm font-semibold text-brand-magnolia">{displayAddress}</span>
                  </button>

                  <button
                    className="bg-grayscale-900 text-grayscale-400 p-2.5 rounded-lg xl:hidden"
                    onClick={() => {
                      navigator.clipboard.writeText(paymentAddress?.address || '');
                    }}
                  >
                    <IconSvg icon="copyS" />
                  </button>

                  <Link
                    to="/profile"
                    onClick={() => setIsMenuOpen(false)}
                    className="bg-grayscale-900 text-grayscale-400 p-2.5 rounded-lg xl:hidden"
                  >
                    <IconSvg icon="userS" />
                  </Link>

                  <button
                    className="bg-grayscale-900 text-grayscale-400 p-2.5 rounded-lg xl:hidden"
                    onClick={() => {
                      disconnect();
                      setIsMenuOpen(false);
                    }}
                  >
                    <IconSvg icon="logoutS" />
                  </button>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() => {
                    setIsSelectWalletOpen(true);
                    setIsMenuOpen(false);
                  }}
                >
                  Connect Wallet
                </Button>
              )}

              <Button
                variant="primary"
                size="xs"
                onClick={() => redirectAndCloseMenu('/raffle/create')}
              >
                Create Raffle
              </Button>
            </div>

            <div className="my-6 w-full border-t border-grayscale-600"></div>

            <div className="space-y-3">
              <Link
                to="/raffle"
                className="flex gap-2 items-center py-3 text-lg font-semibold text-grayscale-300"
                onClick={() => setIsMenuOpen(false)}
              >
                <IconSvg icon="ticketM" />

                <span>Raffles</span>
              </Link>

              <Link
                to="/leaderboard"
                className="flex gap-2 items-center py-3 text-lg font-semibold text-grayscale-300"
                onClick={() => setIsMenuOpen(false)}
              >
                <IconSvg icon="leaderboardM" />

                <span>Leaderboard</span>
              </Link>

              <button className="flex gap-2 items-center py-3 text-lg font-semibold text-brand-granite">
                <span>[Redacted]</span>
              </button>
            </div>
          </div>

          <div
            className="bg-[#1B1B1F] bg-opacity-60 backdrop-blur-sm flex-1"
            onClick={toggleMenu}
          ></div>
        </div>
      )}
    </div>
  );
};

export default AppHeader;
